/**
 * BAN GIA LAP CUA LOI - hien thuc PdfCore chi bang pdf.js.
 *
 * Day la MOI NOI (seam) de ban WASM cua loi Rust cam vao sau nay. Tat ca phan
 * con lai cua ung dung chi biet den giao dien PdfCore, khong biet pdf.js.
 *
 * Lam duoc THAT (khong phai gia):
 *   - mo tai lieu, doc thong tin trang, kich thuoc, do xoay
 *   - ket xuat trang ra bitmap
 *   - trich xuat khoi van ban va CHAN DOAN FONT that tu du lieu font pdf.js nap
 *
 * KHONG lam duoc, va tra ve loi 'unsupported-feature' mot cach ro rang:
 *   - apply(), save(), applyRedactions()
 * Ly do: pdf.js khong co duong ghi. Viet PDF bang JavaScript o day se sinh ra
 * mot duong ghi thu hai canh PDFium va chac chan se phan ky. Duong ghi CHI
 * thuoc ve loi Rust/PDFium.
 */
import type {
  DocumentId,
  DocumentInfo,
  EditCommand,
  FontDiagnostics,
  PageInfo,
  PdfCore,
  PdfError,
  PdfErrorCode,
  PdfMatrix,
  PdfRect,
  RedactionArea,
  RedactionReport,
  Result,
  SaveOptions,
  TextBlock,
  TextRun,
} from '@pdfeditor/types';

import { loadDocument, pdfjsErrorName } from './pdfjs';
import type { PDFDocumentProxy, PDFPageProxy } from './pdfjs';
import {
  diagnoseFont,
  worstEditability,
  type CoreCapabilities,
  type PdfJsObjectStore,
} from './fontDiagnostics';
import { groupRunsIntoLines, unionRect, type RunLine } from './edit/scope';

// ---------------------------------------------------------------------------
// Tien ich ket qua
// ---------------------------------------------------------------------------

function ok<T>(value: T): Result<T> {
  return { ok: true, value };
}

function fail(code: PdfErrorCode, message: string, userMessage: string): Result<never> {
  const error: PdfError = { code, message, userMessage };
  return { ok: false, error };
}

/** Loi dung chung cho moi thao tac ghi trong ban gia lap. */
function writePathUnsupported(what: string): Result<never> {
  return fail(
    'unsupported-feature',
    `${what} requires the Rust/PDFium core; the pdf.js mock is read-only`,
    `Thao tác "${what}" cần lõi Rust/PDFium. Bản web hiện đang chạy lõi giả lập chỉ đọc: ` +
      `pdf.js không có đường ghi, và việc tự ghi PDF bằng JavaScript sẽ tạo ra một đường ghi thứ hai ` +
      `phân kỳ với PDFium. Hãy dùng bản desktop, hoặc chờ bản WASM của lõi.`,
  );
}

// ---------------------------------------------------------------------------
// Hinh hoc: pdf.js -> he toa do PDF
// ---------------------------------------------------------------------------

/**
 * pdf.js tra ve transform cua text item TRONG HE TOA DO PDF (goc duoi trai).
 * transform = [a, b, c, d, e, f]; (e, f) la goc duong co so cua doan chu.
 */
function itemMatrix(transform: number[]): PdfMatrix {
  return [
    transform[0] ?? 1,
    transform[1] ?? 0,
    transform[2] ?? 0,
    transform[3] ?? 1,
    transform[4] ?? 0,
    transform[5] ?? 0,
  ];
}

/** Co chu thuc te = do dai vector doc cua ma tran chu. */
function fontSizeFromMatrix(m: PdfMatrix): number {
  const size = Math.hypot(m[2], m[3]);
  return size > 0 ? size : Math.hypot(m[0], m[1]);
}

// unionRect va phep gom run thanh dong (pha 1) song o edit/scope.ts. Ly do:
// tang giao dien phai bam trung DUNG nhung dong nay de chon pham vi "Dòng", va
// hop dong kieu khong cho TextBlock mang danh sach dong. Chep lai nguong sang
// ben kia se lech - dung chung mot ham thi khong the lech.

// ---------------------------------------------------------------------------
// Trang thai noi bo
// ---------------------------------------------------------------------------

interface OpenDoc {
  id: string;
  pdf: PDFDocumentProxy;
  info: DocumentInfo;
  pageCache: Map<number, Promise<PDFPageProxy>>;
}

let docCounter = 0;

/**
 * Kieu du lieu cua mot muc trong textContent.items. pdf.js tron hai loai
 * (TextItem va TextMarkedContent) trong cung mot mang.
 */
interface RawTextItem {
  str: string;
  transform: number[];
  width: number;
  height: number;
  fontName: string;
  hasEOL: boolean;
}

function isTextItem(item: unknown): item is RawTextItem {
  return (
    typeof item === 'object' &&
    item !== null &&
    'str' in item &&
    'transform' in item &&
    Array.isArray((item as { transform: unknown }).transform)
  );
}

// ---------------------------------------------------------------------------
// Ban gia lap
// ---------------------------------------------------------------------------

export class PdfCoreMock implements PdfCore {
  /**
   * Nhan cho giao dien biet day khong phai loi that.
   * Lop con ghi de gia tri nay - xem PdfCoreBrowser.
   */
  readonly kind: 'mock-pdfjs' | 'browser-pdflib' = 'mock-pdfjs';

  private readonly docs = new Map<string, OpenDoc>();

  /**
   * Nang luc cua loi nay, dung khi chan doan font.
   *
   * Ban gia lap khong sua duoc gi nen khong co font du phong. Lop con
   * (PdfCoreBrowser) ghi de ham nay: no CO font dong goi, va vi vay cung mot
   * font subset se duoc xep hang "sua duoc" thay vi "khong sua duoc". Diem noi
   * nam o day thay vi trong diagnoseFont de chi mot nguon su that duy nhat
   * quyet dinh cau tra loi.
   */
  protected capabilities(): CoreCapabilities {
    return { bundledFontAvailable: false };
  }

  async open(bytes: Uint8Array, password?: string): Promise<Result<DocumentId>> {
    try {
      const task = loadDocument(bytes, password);
      const pdf = await task.promise;
      const info = await this.probeInfo(pdf);
      const id = `doc-${++docCounter}`;
      this.docs.set(id, { id, pdf, info, pageCache: new Map() });
      return ok({ id });
    } catch (err) {
      const name = pdfjsErrorName(err);
      if (name === 'PasswordException') {
        // pdf.js phan biet "can mat khau" va "sai mat khau" qua ma so noi bo.
        const code = password ? 'wrong-password' : 'encrypted-no-password';
        return fail(
          code,
          `pdf.js PasswordException (password supplied: ${Boolean(password)})`,
          password
            ? 'Mật khẩu không đúng. Vui lòng thử lại.'
            : 'Tài liệu này được mã hóa. Vui lòng nhập mật khẩu để mở.',
        );
      }
      return fail(
        'parse-failed',
        `pdf.js failed to parse document: ${String(err)}`,
        'Không đọc được tệp PDF này. Tệp có thể đã hỏng hoặc không phải là PDF hợp lệ.',
      );
    }
  }

  async close(doc: DocumentId): Promise<void> {
    const entry = this.docs.get(doc.id);
    if (!entry) return;
    this.docs.delete(doc.id);
    // Giai phong worker va bo nho bitmap. Bo qua buoc nay la ro ri bo nho
    // nghiem trong nhat cua pdf.js.
    await entry.pdf.destroy().catch(() => undefined);
  }

  async info(doc: DocumentId): Promise<Result<DocumentInfo>> {
    const entry = this.docs.get(doc.id);
    if (!entry) return this.noSuchDoc();
    return ok(entry.info);
  }

  /**
   * Ket xuat trang ra ImageData. Chi dung khi ai do that su can pixel duoi
   * dang du lieu; duong hien thi tren man hinh KHONG di qua day - CanvasLayer
   * ve thang vao canvas cua no de tranh mot lan sao chep 10-30MB moi trang.
   */
  async renderPage(
    doc: DocumentId,
    pageIndex: number,
    scale: number,
  ): Promise<Result<ImageData>> {
    const entry = this.docs.get(doc.id);
    if (!entry) return this.noSuchDoc();
    if (pageIndex < 0 || pageIndex >= entry.info.pageCount) return this.outOfRange(pageIndex);

    try {
      const page = await this.getPage(entry, pageIndex);
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.ceil(viewport.width));
      canvas.height = Math.max(1, Math.ceil(viewport.height));
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) {
        return fail(
          'engine-panic',
          'could not acquire 2d context',
          'Trình duyệt không cấp được ngữ cảnh vẽ 2D. Có thể tăng tốc phần cứng đang bị tắt.',
        );
      }
      await page.render({ canvasContext: ctx, viewport }).promise;
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
      // Tra canvas ve 0x0 de trinh duyet thu hoi bo nho ngay.
      canvas.width = 0;
      canvas.height = 0;
      return ok(data);
    } catch (err) {
      return fail(
        'engine-panic',
        `render failed: ${String(err)}`,
        `Không kết xuất được trang ${pageIndex + 1}.`,
      );
    }
  }

  /**
   * Gom cac muc van ban cua pdf.js thanh khoi sua duoc, kem chan doan font.
   *
   * Thuat toan gom nhom o day la ban rut gon cua thuat toan trong loi that:
   * gom theo duong co so, roi gom cac dong lien nhau co chong lan ngang.
   * Loi Rust se dung them thu tu trong content stream va cay cau truc tagged PDF.
   */
  async textBlocks(doc: DocumentId, pageIndex: number): Promise<Result<TextBlock[]>> {
    const entry = this.docs.get(doc.id);
    if (!entry) return this.noSuchDoc();
    if (pageIndex < 0 || pageIndex >= entry.info.pageCount) return this.outOfRange(pageIndex);

    try {
      const page = await this.getPage(entry, pageIndex);
      const content = await page.getTextContent();
      const styles = content.styles as Record<string, { fontFamily?: string } | undefined>;

      // 1. Muc -> TextRun
      const runs: TextRun[] = [];
      let runCounter = 0;
      for (const raw of content.items) {
        if (!isTextItem(raw)) continue;
        if (raw.str.length === 0) continue;
        const matrix = itemMatrix(raw.transform);
        const fontSizePt = fontSizeFromMatrix(matrix);
        const height = raw.height > 0 ? raw.height : fontSizePt;
        // (e, f) la duong co so; hop bao phu keo xuong duoi mot khoang chan chu.
        const bbox: PdfRect = {
          x: matrix[4],
          y: matrix[5] - height * 0.22,
          width: raw.width,
          height,
        };
        runs.push({
          id: `p${pageIndex}-r${runCounter++}`,
          pageIndex,
          text: raw.str,
          bbox,
          fontRef: raw.fontName,
          fontSizePt,
          // pdf.js khong cong bo Tc / Tw / Th / mau to qua getTextContent().
          // Loi Rust/PDFium doc duoc truc tiep tu trang thai do hoa.
          charSpacing: 0,
          wordSpacing: 0,
          horizontalScale: 100,
          colorRgb: [0, 0, 0],
          matrix,
        });
      }

      // 2. Gom theo dong (cung duong co so trong sai so).
      // Nguong KHONG DOI - xem groupRunsIntoLines trong edit/scope.ts.
      const lines = groupRunsIntoLines(runs);

      // 3. Gom dong thanh khoi: khoang cach doc nho va co chong lan ngang.
      const blocks: TextBlock[] = [];
      let current: RunLine[] = [];
      const flush = () => {
        if (current.length === 0) return;
        const blockRuns = current.flatMap((l) => l.runs);
        let bbox = current[0]!.bbox;
        for (const l of current) bbox = unionRect(bbox, l.bbox);
        const fonts = this.diagnoseBlockFonts(page, blockRuns, styles);
        blocks.push({
          id: `p${pageIndex}-b${blocks.length}`,
          pageIndex,
          runs: blockRuns,
          bbox,
          editability: worstEditability(fonts.map((f) => f.editability)),
          fonts,
        });
        current = [];
      };

      for (const line of lines) {
        const prev = current[current.length - 1];
        if (!prev) {
          current.push(line);
          continue;
        }
        const gap = prev.baseline - line.baseline;
        const maxGap = Math.max(prev.lineHeight, line.lineHeight) * 2.0;
        const overlaps =
          line.bbox.x < prev.bbox.x + prev.bbox.width &&
          prev.bbox.x < line.bbox.x + line.bbox.width;
        if (gap > 0 && gap <= maxGap && overlaps) {
          current.push(line);
        } else {
          flush();
          current.push(line);
        }
      }
      flush();

      return ok(blocks);
    } catch (err) {
      return fail(
        'parse-failed',
        `text extraction failed: ${String(err)}`,
        `Không trích xuất được văn bản của trang ${pageIndex + 1}.`,
      );
    }
  }

  // --- Duong GHI: khong ho tro trong ban gia lap ---------------------------

  async apply(_doc: DocumentId, command: EditCommand): Promise<Result<void>> {
    return writePathUnsupported(command.kind);
  }

  async save(_doc: DocumentId, _options?: SaveOptions): Promise<Result<Uint8Array>> {
    return writePathUnsupported('lưu tệp');
  }

  async applyRedactions(
    _doc: DocumentId,
    _areas: RedactionArea[],
  ): Promise<Result<RedactionReport>> {
    // Che thong tin dac biet khong duoc phep gia lap: mot ban "che" nua voi
    // se cho nguoi dung cam giac an toan sai lam, va do la mau ro ri du lieu
    // da xay ra nhieu lan trong thuc te.
    return writePathUnsupported('che thông tin (redaction)');
  }

  // --- Ho tro cho tang hien thi -------------------------------------------

  /**
   * Loi ra doi tuong trang cua pdf.js.
   * CHI danh cho lop hien thi trong ban web (canvas + lop van ban). Day la
   * cua so ro ri co chu dich: khi loi WASM thay the ban nay, cua so bien mat
   * va lop hien thi chuyen sang renderPage() cua loi.
   */
  async getPageProxy(doc: DocumentId, pageIndex: number): Promise<PDFPageProxy | null> {
    const entry = this.docs.get(doc.id);
    if (!entry) return null;
    if (pageIndex < 0 || pageIndex >= entry.info.pageCount) return null;
    return this.getPage(entry, pageIndex);
  }

  // --- Noi bo -------------------------------------------------------------

  private getPage(entry: OpenDoc, pageIndex: number): Promise<PDFPageProxy> {
    const cached = entry.pageCache.get(pageIndex);
    if (cached) return cached;
    const promise = entry.pdf.getPage(pageIndex + 1);
    entry.pageCache.set(pageIndex, promise);
    return promise;
  }

  private diagnoseBlockFonts(
    page: PDFPageProxy,
    runs: readonly TextRun[],
    styles: Record<string, { fontFamily?: string } | undefined>,
  ): FontDiagnostics[] {
    const commonObjs = page.commonObjs as unknown as PdfJsObjectStore;
    const byFont = new Map<string, string>();
    for (const run of runs) {
      byFont.set(run.fontRef, (byFont.get(run.fontRef) ?? '') + run.text);
    }
    const out: FontDiagnostics[] = [];
    for (const [fontKey, sample] of byFont) {
      const declared = styles[fontKey]?.fontFamily ?? fontKey;
      out.push(diagnoseFont(commonObjs, fontKey, declared, sample, this.capabilities()));
    }
    return out;
  }

  /**
   * Doc kich thuoc TAT CA cac trang ngay khi mo.
   *
   * Bat buoc phai lam truoc: chieu cao vung cuon phai on dinh tu dau, neu khong
   * thanh cuon se nhay lien tuc khi tai lieu duoc phan tich dan. Doc kich thuoc
   * trang chi can phan tich tu dien trang, khong phai content stream - re.
   *
   * hasTextLayer thi NGUOC LAI: phai chay het content stream. Ta chi tham do
   * mot so trang rai deu va suy ra cho phan con lai, roi lop van ban se tu
   * xac minh lai khi trang do that su duoc hien thi.
   */
  private async probeInfo(pdf: PDFDocumentProxy): Promise<DocumentInfo> {
    const pageCount = pdf.numPages;
    const pages: PageInfo[] = [];

    for (let i = 0; i < pageCount; i++) {
      const page = await pdf.getPage(i + 1);
      const view = page.view; // [x0, y0, x1, y1] trong don vi point
      const widthPt = Math.abs((view[2] ?? 612) - (view[0] ?? 0));
      const heightPt = Math.abs((view[3] ?? 792) - (view[1] ?? 0));
      const rotation = (((page.rotate % 360) + 360) % 360) as 0 | 90 | 180 | 270;
      pages.push({
        index: i,
        widthPt,
        heightPt,
        rotation: rotation === 0 || rotation === 90 || rotation === 180 || rotation === 270
          ? rotation
          : 0,
        hasTextLayer: true, // tam thoi; duoc sua lai ngay ben duoi
      });
    }

    // Tham do toi da 12 trang rai deu.
    //
    // Ghi lai CHINH XAC trang nao da tham do thay vi suy lai bang phep chia du
    // "i % step": voi pageCount = 100 thi step = 8, vong lap dung o trang 88
    // (du 12 lan), nhung dieu kien i % step === 0 lai coi ca trang 96 la da
    // tham do. Ket qua: mot trang mang ket qua tham do bi ghi de bang da so,
    // va mot trang chua tham do lai giu nguyen gia tri khoi tao. Mot tap hop
    // tuong minh khong the sai lech kieu do.
    const probeCount = Math.min(12, pageCount);
    const step = Math.max(1, Math.floor(pageCount / probeCount));
    const probedPages = new Set<number>();
    let withText = 0;
    for (let i = 0; i < pageCount && probedPages.size < probeCount; i += step) {
      probedPages.add(i);
      try {
        const page = await pdf.getPage(i + 1);
        const content = await page.getTextContent();
        // CHI dem, KHONG giu lai chuoi van ban: ham nay chay tren moi tai lieu
        // nguoi dung mo, va noi dung tai lieu khong duoc phep ro ri ra bat ky
        // bao cao / nhat ky / doi tuong trang thai nao.
        const has = content.items.some((it) => isTextItem(it) && it.str.trim().length > 0);
        const target = pages[i];
        if (target) target.hasTextLayer = has;
        if (has) withText++;
      } catch {
        // Tham do that bai thi KHONG duoc tinh vao mau: mot trang loi khong
        // phai bang chung cho "khong co lop van ban". Bo ra khoi tap da tham
        // do de no nhan gia tri theo da so.
        probedPages.delete(i);
      }
    }
    const probed = probedPages.size;
    const majority = probed === 0 ? true : withText * 2 >= probed;
    for (let i = 0; i < pageCount; i++) {
      if (probedPages.has(i)) continue;
      const target = pages[i];
      if (target) target.hasTextLayer = majority;
    }

    let pdfVersion = '1.7';
    let encrypted = false;
    let signed = false;
    let tagged = false;
    let hasXfa = false;
    let linearized = false;

    try {
      const meta = await pdf.getMetadata();
      const raw = meta.info as Record<string, unknown> | undefined;
      if (raw) {
        if (typeof raw['PDFFormatVersion'] === 'string') pdfVersion = raw['PDFFormatVersion'];
        encrypted = raw['IsEncrypted'] === true;
        linearized = raw['IsLinearized'] === true;
        hasXfa = raw['IsXFAPresent'] === true;
        signed = raw['IsSignaturesPresent'] === true;
        tagged = raw['IsTaggedPDF'] === true;
      }
    } catch {
      // Thieu metadata khong phai loi nghiem trong.
    }

    return { pageCount, pages, encrypted, signed, tagged, hasXfa, linearized, pdfVersion };
  }

  private noSuchDoc(): Result<never> {
    return fail(
      'io-error',
      'unknown document id',
      'Tài liệu không còn mở. Vui lòng mở lại tệp.',
    );
  }

  private outOfRange(pageIndex: number): Result<never> {
    return fail(
      'page-out-of-range',
      `page ${pageIndex} out of range`,
      `Trang ${pageIndex + 1} không tồn tại trong tài liệu này.`,
    );
  }
}
